import {
  afterNextRender,
  Component,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { DestroyableComponent } from '../../../shared/components/destroyable/destroyable.component';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  take,
  takeUntil,
} from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { FormControl, FormGroup, UntypedFormGroup } from '@angular/forms';
import { InputOption } from '../../../core/models/common/input-option';
import { InputOptionsHelper } from '../../../core/helpers/input-options.helper';
import * as fromStore from '../../../core/store/reducers';
import {
  selectPublicVendor,
  selectVendorLoadingError,
} from '../../../core/store/reducers';
import { Store } from '@ngrx/store';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { isFuture, isToday, parseISO, startOfToday } from 'date-fns';
import { DatetimeHelper } from '../../../core/helpers/datetime.helper';
import { Room, RoomAvailability } from '../../../core/models/entities/room';
import { fadeInOnceRenderedInBrowser } from '../../../core/animations/fade.animation';
import {
  EventType,
  EventTypeId,
} from '../../../core/models/entities/event-type';
import { RoomAmenity } from '../../../core/models/entities/room-amenity';
import { AmenityId } from '../../../core/models/entities/amenity';
import { ToastService } from '../../../core/services/toast.service';
import { Title } from '@angular/platform-browser';
import { availableRoomsActions } from 'angular/app/core/store/available-rooms';
import { cloneDeep } from 'lodash';
import { RoomsPeopleFilter } from '../../../core/store/bookings/bookings.state';
import { AvailableRoomsFilter } from '../../../core/store/available-rooms/available-rooms.state';
import { BaseOptions } from 'flatpickr/dist/types/options';
import { RoomsService } from '../../../core/services/rooms.service';
import { LocalizedUrlService } from '../../../core/services/localized-url.service';

@Component({
  selector: 'app-vendor-page',
  animations: [fadeInOnceRenderedInBrowser],
  template: `
    <div class="bg-white">
      <div class="container py-6 lg:py-12">
        @if (vendorLoadingError()) {
          <div class="mx-auto w-80 md:w-96">
            <dotlottie-wc
              autoplay
              loop
              src="/assets/img/404.json"
            ></dotlottie-wc>
          </div>
          <div class="text-center">
            <div class="-mt-8 text-6xl font-bold uppercase">{{ 'vendor-page.error.title' | transloco }}</div>
            <div class="mt-4 text-lg font-bold uppercase">
              {{ 'vendor-page.error.subtitle' | transloco }}
            </div>
            <p class="mt-4 text-base">
              {{ 'vendor-page.error.description' | transloco }}
            </p>

            <div class="mt-10">
              <app-button [routerLink]="searchLink()" size="sm" icon="arrow-right"
                >{{ 'vendor-page.error.search-button' | transloco }}</app-button
              >
            </div>
          </div>
        } @else {
          <div>
            <!-- ------------------------------------------------------------------------->
            <!-- Header hero : skeleton pendant le chargement, contenu réel ensuite -->
            <!-- ------------------------------------------------------------------------->
            <div class="grid grid-cols-1 items-center lg:grid-cols-2 lg:gap-20">
              <div>
                @if (vendor()) {
                  @if (vendor().logo_url) {
                    <img
                      class="mb-10 max-h-[68px]"
                      [src]="vendor().logo_url"
                      alt="Logo {{ vendor().name }}"
                      title="Logo {{ vendor().name }}"
                    />
                  }

                  <h1
                    >{{ 'vendor-page.hero-title' | transloco }}<br /><span class="font-normal italic">{{
                      vendor().name
                    }}</span></h1
                  >
                } @else {
                  <div>
                    <app-skeleton-loader
                      class="mb-10 block"
                      width="180px"
                      height="68px"
                    ></app-skeleton-loader>
                    <app-skeleton-loader
                      class="block"
                      width="55%"
                      height="48px"
                    ></app-skeleton-loader>
                    <app-spacer size="3"></app-spacer>
                    <app-skeleton-loader
                      class="block"
                      width="75%"
                      height="48px"
                    ></app-skeleton-loader>
                  </div>
                }
              </div>

              <div class="col-span-1 hidden xl:flex xl:justify-center">
                @if (vendor()) {
                  <img
                    class="max-h-[450px]"
                    ngSrc="vendor-hero.png"
                    width="460"
                    height="450"
                    sizes="25vw"
                    [attr.alt]="'vendor-page.hero-image-alt' | transloco: { name: vendor().name }"
                    [attr.title]="'vendor-page.hero-image-alt' | transloco: { name: vendor().name }"
                  />
                } @else {
                  <app-skeleton-loader
                    width="460px"
                    height="450px"
                  ></app-skeleton-loader>
                }
              </div>
            </div>

            <!-- ------------------------------------------------------------------------->
            <!-- Formulaire de recherche (toujours affiché immédiatement) -->
            <!-- ------------------------------------------------------------------------->
            <div
              class="border-gray-medium mt-10 rounded-lg border p-6 lg:p-8 lg:pb-4 xl:mt-20"
            >
              <form novalidate (ngSubmit)="onSubmit()">
                <div class="grid grid-cols-2 items-center gap-x-6">
                  <div class="col-span-2 md:col-span-1">
                    <app-date-input
                      [label]="'vendor-page.search.date-label' | transloco"
                      name="start"
                      [control]="form.get('start')"
                      [flatpickrOptions]="startOptions"
                      [placeholder]="'vendor-page.search.date-placeholder' | transloco"
                    ></app-date-input>
                  </div>
                  <div class="col-span-2 md:col-span-1">
                    <div class="md:translate-y-8 lg:translate-y-4">
                      <app-single-checkbox-input
                        [label]="'vendor-page.search.flexible-label' | transloco"
                        name="flexible"
                        [control]="form.get('flexible')"
                      ></app-single-checkbox-input>
                    </div>
                  </div>
                </div>
                <div class="items-end lg:flex lg:space-x-6">
                  <div class="lg:basis-1/3 xl:basis-1/2">
                    <app-select-input
                      [label]="'vendor-page.search.event-type-label' | transloco"
                      name="event_type_id"
                      [options]="eventTypesOptions$ | async"
                      [hidePleaseSelect]="true"
                      [control]="form.get('event_type_id')"
                    ></app-select-input>
                  </div>
                  <div class="lg:grow">
                    <app-select-input
                      [label]="'vendor-page.search.seats-label' | transloco"
                      name="seats"
                      [hidePleaseSelect]="true"
                      [options]="seatsOptions"
                      [control]="form.get('seats')"
                    ></app-select-input>
                  </div>
                  <div class="lg:-translate-y-6">
                    <app-button type="submit" [fullWidthOnMobile]="true"
                      >{{ 'vendor-page.search.submit-button' | transloco }}
                    </app-button>
                  </div>
                </div>
              </form>
            </div>

            <!-- ------------------------------------------------------------------------->
            <!-- Liste des salles -->
            <!-- ------------------------------------------------------------------------->
            <div class="mt-10 md:mt-16">
              @if (fetching$ | async) {
                <div class="grid gap-10 md:grid-cols-2 xl:grid-cols-3">
                  @for (placeholder of skeletonPlaceholders; track $index) {
                    <app-room-card-skeleton></app-room-card-skeleton>
                  }
                </div>
              }
              @if ((fetching$ | async) === false) {
                <div [@fadeInOnceRenderedInBrowser]="roomListAnimationState()">
                  @if ((rooms$ | async)?.length === 0) {
                    <div class="pt-20">
                      <app-empty-state
                        icon="calendar-times"
                        [message]="'vendor-page.rooms.empty-message' | transloco"
                        [inline]="true"
                        [showAddButton]="false"
                      ></app-empty-state>
                    </div>
                  }
                  @if ((rooms$ | async)?.length > 0) {
                    <div class="grid gap-10 md:grid-cols-2 xl:grid-cols-3">
                      @for (room of rooms$ | async; track room) {
                        <div
                          class="shadow-sidebar col-span-1 flex flex-col rounded-xl"
                          [routerLink]="roomLink(room.id)"
                        >
                          <div class="flex">
                            <div class="cursor-pointer">
                              @if (room.photos[0] || !!room.default_photo) {
                                <img
                                  [attr.src]="
                                    room.photos[0]?.public_path ??
                                    room.default_photo
                                  "
                                  alt="{{ room.name }}"
                                  title="{{ room.name }}"
                                  class="rounded-tl-xl rounded-br-xl"
                                />
                              }
                            </div>
                            <div
                              class="flex w-14 flex-none flex-col items-center justify-between py-2"
                            >
                              @for (
                                icon of roomSelectedIcons.get(room.id);
                                track $index
                              ) {
                                <div class="flex basis-1/4 items-center">
                                  @if (icon) {
                                    <i class="fal fa-lg fa-{{ icon }}"></i>
                                  }
                                </div>
                              }
                            </div>
                          </div>
                          <div
                            class="flex grow flex-col justify-between gap-8 p-8"
                          >
                            <div class="space-y-4">
                              <div>
                                <h3 class="text-lg! font-bold">{{
                                  room.name
                                }}</h3>
                                @if (room.building) {
                                  <div class="italic">{{
                                    room.building.name
                                  }}</div>
                                }
                              </div>
                              <div class="ml-2 space-y-3">
                                <div class="flex items-center">
                                  <div class="w-8 flex-none">
                                    <i class="fal fa-users"></i>
                                  </div>
                                  {{ 'vendor-page.rooms.occupancy' | transloco: { count: room.max_occupancy } }}
                                </div>
                                <div class="flex">
                                  <div class="w-8 flex-none">
                                    <i class="fal fa-info-circle"></i>
                                  </div>
                                  <div class="relative -top-px">
                                    @for (
                                      eventType of room.event_types;
                                      track eventType;
                                      let last = $last
                                    ) {
                                      <span>{{ $any(eventType) | translated: 'name' }}{{ last ? '' : ', ' }}</span>
                                    }
                                  </div>
                                </div>
                              </div>
                              @if (room.availability) {
                                <div class="pt-3">
                                  @if (
                                    room.availability === RoomAvailability.Free
                                  ) {
                                    <app-badge variant="success"
                                      >{{ 'vendor-page.rooms.availability.free' | transloco }}
                                    </app-badge>
                                  }
                                  @if (
                                    room.availability ===
                                    RoomAvailability.Partial
                                  ) {
                                    @if (isPartiallyBookable(room)) {
                                      <app-badge variant="success"
                                        >{{
                                          'vendor-page.rooms.availability.partially-bookable'
                                            | transloco
                                        }}
                                      </app-badge>
                                    } @else {
                                      <app-badge variant="warning"
                                        >{{
                                          'vendor-page.rooms.availability.partial' | transloco
                                        }}
                                      </app-badge>
                                    }
                                  }
                                  @if (
                                    room.availability ===
                                    RoomAvailability.Occupied
                                  ) {
                                    <app-badge variant="danger"
                                      >{{ 'vendor-page.rooms.availability.occupied' | transloco }}
                                    </app-badge>
                                  }
                                </div>
                              }
                            </div>
                            <app-button
                              [loading]="fetching$ | async"
                              [attr.data-cy]="'room-details-button'"
                              [routerLink]="roomLink(room.id)"
                              >{{ 'vendor-page.rooms.discover-button' | transloco }}
                            </app-button>
                          </div>
                        </div>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        }
      </div>
    </div>
  `,
  standalone: false,
})
export class VendorPageComponent
  extends DestroyableComponent
  implements OnInit
{
  RoomAvailability = RoomAvailability;
  // Cartes de chargement affichées pendant la recherche de salles
  skeletonPlaceholders = Array.from({ length: 6 });
  roomListAnimationState = signal<'static' | 'animated'>('static');
  // Évite un second appel `available` quand la recherche a déjà été lancée en parallèle (via le slug)
  private initialSearchDispatched = false;
  private formValueChangesSubscribed = false;
  vendor = this.store.selectSignal(selectPublicVendor);
  vendorSlug = this.store.selectSignal(fromStore.selectVendorSlug);
  vendorLoadingError = this.store.selectSignal(selectVendorLoadingError);
  form: UntypedFormGroup;
  roomsService = inject(RoomsService);
  eventTypesOptions$: Observable<InputOption[]>;
  fetching$: Observable<boolean> = this.store.select(
    fromStore.selectAvailableRoomsLoading,
  );
  rooms$: Observable<Room[]> = this.store
    .select(fromStore.selectAllAvailableRooms)
    .pipe(
      map((rooms: Room[]) =>
        rooms.filter((room: Room) => {
          return this.roomsService.canUserAccessRoom(room);
        }),
      ),
    );
  roomSelectedIcons: Map<number, string[]> = new Map<number, string[]>();
  seatsOptions: InputOption[] = [];
  startOptions: Partial<BaseOptions>;

  private translocoService = inject(TranslocoService);
  private localizedUrlService = inject(LocalizedUrlService);

  // Lien localisé vers la page d'une salle du vendor courant
  roomLink(roomId: number): string[] {
    return this.localizedUrlService.link('room', {
      vendorSlug: this.vendorSlug(),
      id: roomId,
    });
  }

  // Lien localisé vers la recherche de salles
  searchLink(): string[] {
    return this.localizedUrlService.link('search');
  }

  constructor(
    private store: Store,
    private formValidationService: FormValidationService,
    private toastService: ToastService,
    private title: Title,
  ) {
    super();

    afterNextRender(() => {
      import('@lottiefiles/dotlottie-wc');
      this.roomListAnimationState.set('animated');
    });

    this.startOptions = { minDate: startOfToday() };
    this.initializeFormWithStoredFilters();

    effect(() => {
      if (this.vendor()) {
        this.title.setTitle(
          this.translocoService.translate('vendor-page.page-title', { name: this.vendor().name }),
        );

        this.form.patchValue(
          { vendor_id: this.vendor().id },
          { emitEvent: false },
        );

        if (!this.initialSearchDispatched) {
          this.findAvailableRooms();
        }

        if (!this.formValueChangesSubscribed) {
          this.formValueChangesSubscribed = true;
          this.form.valueChanges
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(() => this.findAvailableRooms());
        }
      }
    });
  }

  private initializeFormWithStoredFilters(): void {
    let filters: AvailableRoomsFilter | null = null;
    this.store
      .select(fromStore.selectAvailableRoomsState)
      .pipe(take(1))
      .subscribe((state) => (filters = state.filters));

    this.form = new FormGroup({
      vendor_id: new FormControl(null),
      start: new FormControl(
        filters?.start &&
        (isToday(parseISO(filters.start)) || isFuture(parseISO(filters.start)))
          ? [parseISO(filters.start)]
          : [],
      ),
      end: new FormControl(),
      flexible: new FormControl(filters?.flexible ?? true),
      event_type_id: new FormControl(filters?.event_type_id ?? ''),
      seats: new FormControl(filters?.seats ?? 1),
    });
  }

  ngOnInit(): void {
    this.eventTypesOptions$ = combineLatest([
      InputOptionsHelper.selectOptions$(this.store.select(fromStore.selectAllEventTypes)),
      this.translocoService.selectTranslation(),
    ]).pipe(
      map(([options]) => {
        const result = [...options];
        result.unshift({ label: this.translocoService.translate('vendor-page.search.event-type-all'), value: '' });
        return result;
      }),
    );

    this.translocoService.selectTranslation().pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.buildSeatsOptions();
    });

    this.searchAvailableRoomsOnVendorSlugChange();

    this.store
      .select(fromStore.selectAvailableRoomsState)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((state) => {
        if (!!state.error) {
          this.toastService.error(state.error);
        }
      });

    this.rooms$.pipe(takeUntil(this.onDestroy$)).subscribe((rooms) => {
      this.roomSelectedIcons.clear();
      rooms.forEach((room: Room) => {
        this.roomSelectedIcons.set(
          room.id,
          this.getFavoriteAmenitiesFromEventType(cloneDeep(room)),
        );
      });
    });
  }

  onSubmit() {
    if (this.formValidationService.validate(this.form)) {
      this.findAvailableRooms();
    }
  }

  /**
   * Une salle partiellement réservée n'est réellement « Partiellement disponible »
   * que si l'on peut réserver le temps restant (location à l'heure ou par créneau).
   * Sinon (réservable à la journée uniquement), on conserve « Dates alternatives disponibles ».
   */
  isPartiallyBookable(room: Room): boolean {
    return room.hourly_rate_enabled || room.uses_time_slots;
  }

  private getFavoriteAmenitiesFromEventType(room: Room): string[] {
    let selectedRoomAmenities: RoomAmenity[] = [];
    if (room.event_types.length > 1) {
      selectedRoomAmenities = room.room_amenities;
    } else if (room.event_types.length === 1) {
      const eventType: EventType = room.event_types[0];
      let favoriteAmenitiesIds: number[] = [];
      switch (eventType.id) {
        case EventTypeId.CONFERENCE:
          favoriteAmenitiesIds = [
            AmenityId.AUDIOVISUAL,
            AmenityId.PARKING,
            AmenityId.WIFI,
            AmenityId.FURNITURE,
          ];
          break;
        case EventTypeId.PRIVATE_EVENT:
          favoriteAmenitiesIds = [
            AmenityId.KITCHEN,
            AmenityId.SILVERWARE,
            AmenityId.PARKING,
            AmenityId.REDUCED_MOBILITY,
          ];
          break;
        case EventTypeId.SPORT:
          favoriteAmenitiesIds = [
            AmenityId.RESTROOMS,
            AmenityId.FURNITURE,
            AmenityId.PARKING,
          ];
          break;
        case EventTypeId.CULTURE:
          favoriteAmenitiesIds = [
            AmenityId.KITCHEN,
            AmenityId.STAGE,
            AmenityId.AUDIOVISUAL,
            AmenityId.PARKING,
          ];
          break;
      }
      selectedRoomAmenities = room.room_amenities.filter(
        (roomAmenity: RoomAmenity) => {
          return (
            favoriteAmenitiesIds.includes(roomAmenity.amenity.id) &&
            roomAmenity.amenity_id !== AmenityId.OTHER
          );
        },
      );
      if (selectedRoomAmenities.length < 4) {
        const otherRoomAmenities: RoomAmenity[] = room.room_amenities.filter(
          (roomAmenity: RoomAmenity) => {
            return !favoriteAmenitiesIds.includes(roomAmenity.amenity.id);
          },
        );
        otherRoomAmenities.forEach((roomAmenity: RoomAmenity) => {
          if (selectedRoomAmenities.length < 4) {
            selectedRoomAmenities.push(roomAmenity);
          }
        });
      }
    }

    selectedRoomAmenities = this.shuffleIdenticallyOnServerAndBrowser(
      selectedRoomAmenities,
      room.id,
    ).slice(0, 4);
    const icons = selectedRoomAmenities.map((roomAmenity: RoomAmenity) => {
      return roomAmenity.amenity.icon;
    });
    while (icons.length < 4) {
      icons.push(null);
    }

    return icons;
  }

  private shuffleIdenticallyOnServerAndBrowser(
    roomAmenities: RoomAmenity[],
    roomId: number,
  ): RoomAmenity[] {
    const rankOf = (roomAmenity: RoomAmenity) =>
      (roomAmenity.amenity.id * 7919 + roomId * 104729) % 1009;

    return [...roomAmenities].sort(
      (first, second) => rankOf(first) - rankOf(second),
    );
  }

  private buildSeatsOptions(): void {
    this.seatsOptions = [
      { label: this.translocoService.translate('vendor-page.search.seats-no-preference'), value: 1 },
    ].concat(
      // @ts-ignore
      Object.values(RoomsPeopleFilter).map((value) => ({
        label: this.translocoService.translate(
          `vendor-page.search.seats-${value}`,
        ),
        value:
          value === RoomsPeopleFilter.LESS_THAN_20
            ? 19
            : value === RoomsPeopleFilter.BETWEEN_20_AND_100
              ? 100
              : value === RoomsPeopleFilter.MORE_THAN_100
                ? 101
                : 1,
      })),
    );
  }

  private searchAvailableRoomsOnVendorSlugChange(): void {
    this.store
      .select(fromStore.selectVendorSlug)
      .pipe(
        filter((vendorSlug): vendorSlug is string => !!vendorSlug),
        distinctUntilChanged(),
        takeUntil(this.onDestroy$),
      )
      .subscribe((vendorSlug) => this.dispatchAvailableSearch(vendorSlug));
  }

  private dispatchAvailableSearch(vendorSlug: string): void {
    let filters: AvailableRoomsFilter | null = null;
    this.store
      .select(fromStore.selectAvailableRoomsState)
      .pipe(take(1))
      .subscribe((state) => (filters = state.filters));

    this.initialSearchDispatched = true;
    this.store.dispatch(
      availableRoomsActions.setFilters({
        filters: {
          ...filters,
          flexible: filters?.flexible ?? true,
          vendor_slug: vendorSlug,
          vendor_id: undefined,
        },
      }),
    );
  }

  private async findAvailableRooms(): Promise<void> {
    let start = null;
    if (this.form.value.start[0]) {
      start = DatetimeHelper.dateToApiFormat(this.form.value.start[0]);
    }
    let end: string;
    if (this.form.value.end?.[0]) {
      end = DatetimeHelper.dateToApiFormat(this.form.value.end[0]);
    } else if (this.form.value.start[0]) {
      end = DatetimeHelper.dateToApiFormat(this.form.value.start[0]);
    }
    this.store.dispatch(
      availableRoomsActions.setFilters({
        filters: {
          ...this.form.value,
          start,
          end,
        },
      }),
    );
  }
}
